import React, { useState } from 'react';
import { Lock, User as UserIcon, LogIn, UserPlus, AlertCircle, Eye, EyeOff, Layers, Sparkles } from 'lucide-react';
import { ANIME_AVATARS } from '../data/initialCatalog';
import { registerWithUsername, loginWithUsername } from '../lib/firebase';
import { UserProfile } from '../types';

interface LoginScreenProps {
  onSuccess: (user: UserProfile) => void;
}

export const LoginScreen: React.FC<LoginScreenProps> = ({ onSuccess }) => {
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [selectedAvatar, setSelectedAvatar] = useState(ANIME_AVATARS[0].url);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!username.trim()) {
      setError("Si us plau, introdueix un nom d'usuari.");
      return;
    }

    if (password.length < 6) {
      setError("La contrasenya ha de tenir com a mínim 6 caràcters.");
      return;
    }

    setLoading(true);
    try {
      if (mode === 'signup') {
        const user = await registerWithUsername(
          username.trim(),
          password,
          displayName.trim() || username.trim(),
          selectedAvatar
        );
        onSuccess(user);
      } else {
        const user = await loginWithUsername(username.trim(), password);
        onSuccess(user);
      }
    } catch (err: any) {
      console.error('Auth error:', err);
      if (err.code === 'auth/email-already-in-use' || err.code === 'auth/credential-already-in-use') {
        setError("Aquest nom d'usuari ja està registrat. Prova d'iniciar sessió.");
      } else if (err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password' || err.code === 'auth/user-not-found') {
        setError("Nom d'usuari o contrasenya incorrectes.");
      } else {
        setError(err.message || "S'ha produït un error durant l'autenticació.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#070b14] text-slate-100 flex flex-col justify-center items-center px-4 py-12 relative overflow-hidden selection:bg-sky-500 selection:text-black">
      {/* Ambient background glow & radial gradient */}
      <div className="absolute top-1/4 -left-32 w-96 h-96 bg-blue-600/15 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-cyan-500/15 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-[#0d1527]/60 via-[#070b14] to-[#070b14] pointer-events-none" />

      {/* Main Container */}
      <div className="relative z-10 w-full max-w-md">
        
        {/* Brand Header */}
        <div className="text-center mb-8 flex flex-col items-center">
          <div className="flex items-center gap-3 mb-3">
            <div className="relative w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-600 via-indigo-600 to-cyan-400 flex items-center justify-center shadow-xl shadow-sky-500/25">
              <Layers className="w-6 h-6 text-white" />
              <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-cyan-400 border-2 border-black flex items-center justify-center">
                <Sparkles className="w-2.5 h-2.5 text-black" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-display font-black text-3xl sm:text-4xl tracking-tight text-white">
                Gar<span className="text-sky-400">Collections</span>
              </span>
              <span className="px-2 py-0.5 rounded text-[11px] font-black uppercase tracking-wider bg-sky-500/20 text-sky-300 border border-sky-500/30">
                HUB
              </span>
            </div>
          </div>
          <p className="text-xs sm:text-sm text-slate-400 max-w-xs">
            Inicia sessió per gestionar i reproduir les teves pròpies col·leccions de streaming.
          </p>
        </div>

        {/* Login / Signup Card */}
        <div className="bg-[#0c1220]/90 border border-sky-500/30 rounded-3xl p-6 sm:p-8 shadow-2xl backdrop-blur-xl">
          
          {/* Mode Switcher Tabs */}
          <div className="flex bg-white/5 p-1 rounded-xl mb-6 border border-white/5">
            <button
              type="button"
              onClick={() => { setMode('login'); setError(null); }}
              className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${
                mode === 'login'
                  ? 'bg-sky-500 text-black shadow-md'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              Inicia Sessió
            </button>
            <button
              type="button"
              onClick={() => { setMode('signup'); setError(null); }}
              className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${
                mode === 'signup'
                  ? 'bg-sky-500 text-black shadow-md'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              Crear Compte
            </button>
          </div>

          {error && (
            <div className="mb-5 p-3.5 rounded-xl bg-red-500/15 border border-red-500/30 text-red-300 text-xs flex items-center gap-2.5 animate-in fade-in">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            
            {/* Username Input */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-300 flex items-center justify-between">
                <span>Nom d'usuari</span>
                <span className="text-[10px] text-slate-500 font-normal">Sense espais</span>
              </label>
              <div className="relative">
                <UserIcon className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  required
                  value={username}
                  onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/\s+/g, ''))}
                  placeholder="ex: josep, usuari123..."
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-slate-900/90 border border-white/10 text-white text-xs sm:text-sm focus:border-sky-500 focus:outline-none"
                />
              </div>
            </div>

            {/* Display Name (Only on Signup) */}
            {mode === 'signup' && (
              <div className="space-y-1.5 animate-in fade-in">
                <label className="text-xs font-semibold text-slate-300">
                  Nom a mostrar (opcional)
                </label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="ex: Josep G."
                  className="w-full px-4 py-2.5 rounded-xl bg-slate-900/90 border border-white/10 text-white text-xs sm:text-sm focus:border-sky-500 focus:outline-none"
                />
              </div>
            )}

            {/* Avatar Selector (Only on Signup) */}
            {mode === 'signup' && (
              <div className="space-y-1.5 animate-in fade-in pt-1">
                <label className="text-xs font-semibold text-slate-300">
                  Tria el teu Avatar
                </label>
                <div className="flex items-center gap-2 overflow-x-auto py-1 scrollbar-none">
                  {ANIME_AVATARS.map((av) => (
                    <button
                      key={av.id}
                      type="button"
                      onClick={() => setSelectedAvatar(av.url)}
                      className={`relative w-10 h-10 rounded-full overflow-hidden shrink-0 border-2 transition-transform ${
                        selectedAvatar === av.url ? 'border-sky-400 scale-110 shadow-md shadow-sky-500/30' : 'border-white/10 opacity-70 hover:opacity-100'
                      }`}
                    >
                      <img src={av.url} alt={av.name} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Password Input */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-300">
                Contrasenya
              </label>
              <div className="relative">
                <Lock className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínim 6 caràcters"
                  className="w-full pl-10 pr-10 py-2.5 rounded-xl bg-slate-900/90 border border-white/10 text-white text-xs sm:text-sm focus:border-sky-500 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full mt-2 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white font-bold text-xs sm:text-sm shadow-lg shadow-sky-500/25 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <span>Carregant...</span>
              ) : mode === 'login' ? (
                <>
                  <LogIn className="w-4 h-4" />
                  <span>Entrar al Meu Catàleg</span>
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4" />
                  <span>Crear Compte</span>
                </>
              )}
            </button>

          </form>

        </div>

        {/* Footer Note */}
        <p className="text-center text-[11px] text-slate-500 mt-6">
          Base de dades aïllada per usuari a Firestore: <span className="font-mono text-sky-400">garcollections2</span>
        </p>

      </div>
    </div>
  );
};
